// Angular
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router } from '@angular/router';
// Service
import { QualityDocumentService } from './document-quality.service';
import { BroadcastService, SignalRService, UpdateSchemaUrl, ToasterType } from '@cv-invision/core';
import { RuleService } from '../rules/service/rule.service';
// Store
import { Store } from '@ngxs/store';
import { QualityState } from '../state/quality.state';
import { IntelligenceState } from '../../state/intelligence.state';
// Models
import { DOCUMENT_ATTACHMENT_CATEGORIES, SCHEMA_TYPE } from '../model/quality.constant';
import { OrderDetailModel } from '../state/model/order-detail.model';
// Actions
import {
	SetOrdersRules,
	SetRuleSet,
	SetIsRunningRules,
	SetUncheckedRules
} from '../rules/state/action/rules.actions';
import { SelectedOrderID } from '../../inbox/state/action/inbox.action';
import { SetCurrentPhotos } from '../photo-review/state/action/photo-review.action';
import {
	CleanQualityOrder,
	GetOrderRevisedFields,
	SetOrderDocument,
	SetOrderPublicRecord,
	SetSchemaDocument
} from '../state/actions/quality.actions';
import { SetVirtualTour } from '../virtual-tour/state/action/virtual-tour.actions';
import { SetOrderIsImporter } from '../../state/actions/intelligence.actions';
import { SetLeftMenu, ClearLeftMenu } from '../state/actions/quality.actions';
// Others
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DocumentLoaderGuard implements CanActivateChild {
	constructor(
		private router: Router,
		private store: Store,
		private qualityService: QualityDocumentService,
		private rulesService: RuleService,
		private broadcastService: BroadcastService,
		private signalRService: SignalRService
	) {}

	/**
	 * Can activate validation to access any child component in Quality.
	 * @param route
	 */
	canActivateChild(route: ActivatedRouteSnapshot): Promise<boolean> {
		// Get Order Id from the Route
		const orderId = Number(route.params['orderId']);
		// Run Validation and wait for it
		// eslint-disable-next-line no-async-promise-executor
		return new Promise(async resolve => {
			if (!isNaN(orderId) && orderId >= 1) {
				const currentOrderDetailModel: OrderDetailModel = this.store.selectSnapshot(
					QualityState.getOrderDetail
				);
				const orderDocument = this.store.selectSnapshot(QualityState.getOrderDocument);
				if (!currentOrderDetailModel || currentOrderDetailModel.id !== orderId || !orderDocument) {
					// ALWAYS reset the unchecked rules flag to false.
					await this.store.dispatch(new CleanQualityOrder()).toPromise();
					await this.store.dispatch(new SetCurrentPhotos([])).toPromise();
					await this.store.dispatch(new SetUncheckedRules(false)).toPromise();
					await this.store.dispatch(new ClearLeftMenu()).toPromise();

					this.qualityService.forceReloadForCurrentPDF();
					this.qualityService
						.setOrderToReview(orderId)
						.subscribe((orderDetailModel: OrderDetailModel) => {
							if (!orderDetailModel || !orderDetailModel.id || orderDetailModel.deleted) {
								if (orderDetailModel && orderDetailModel.deleted) {
									setTimeout(() => {
										this.broadcastService.broadcast({
											name: ToasterType.ERROR,
											event: {
												error:
													'Duplicated order <span class="order-id">' +
													orderDetailModel.externalId +
													'</span> was deleted.'
											}
										});
									}, 100);
								}
								return resolve(this.rejectToMainModule());
							}
							this.setDocumentOrderToReview(orderDetailModel).then(
								(isReadyToReview: boolean) => {
									if (!isReadyToReview) {
										return resolve(this.rejectToMainModule());
									}
									resolve(isReadyToReview);

								}
							);
						});
				} else {
					if (currentOrderDetailModel.deleted) {
						return resolve(this.rejectToMainModule());
					}
					return resolve(true);
				}
			} else {
				return resolve(this.rejectToMainModule());
			}
		});
	}

	/**
	 * Set Document Order To Review
	 * @param orderDetailModel
	 */
	public setDocumentOrderToReview(orderDetailModel: OrderDetailModel): Promise<boolean> {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise(async resolve => {
			if (orderDetailModel.revised) {
				await this.store.dispatch(new GetOrderRevisedFields(orderDetailModel.id)).toPromise();
			}
			await this.store.dispatch(
				new UpdateSchemaUrl({
					orderId: orderDetailModel.id,
					schema: orderDetailModel.schema,
					revised: orderDetailModel.revised,
					isCMPForm: orderDetailModel.isCMPForm
				})
			).toPromise();
			await this.store.dispatch(
				new SetOrdersRules({
					orderId: orderDetailModel.id,
					documentId: orderDetailModel.documentId,
					revised: orderDetailModel.revised
				})
			).toPromise();
			await this.store.dispatch(new SelectedOrderID(orderDetailModel.id)).toPromise();
			await this.store.dispatch(new SetOrderDocument(orderDetailModel.id)).toPromise();
			await this.store.dispatch(new SetRuleSet(orderDetailModel.id)).toPromise();
			await this.store.dispatch(new SetLeftMenu(orderDetailModel)).toPromise();

			if (this.store.selectSnapshot(IntelligenceState.getOrderIsImported)) {
				await this.store.dispatch(new SetIsRunningRules(true)).toPromise();
				await this.store.dispatch(new SetOrderIsImporter(false)).toPromise();
			}

			await this.store.dispatch(new SetSchemaDocument(orderDetailModel.schema)).toPromise();

			if (orderDetailModel.schema === SCHEMA_TYPE.INSPECTION_R) {
				await this.store.dispatch(new SetVirtualTour(orderDetailModel.id)).toPromise();
			}

			await this.store.dispatch(new SetOrderPublicRecord(orderDetailModel.id)).toPromise();
			await this.rulesService.getAvailableRuleSets(orderDetailModel.id).toPromise();

			await this.documentLoadedSuccess(orderDetailModel.id, orderDetailModel.documentId).toPromise();

			resolve(true);
		});
	}

	/**
	 * After the document has been completely Loaded,
	 * Download the PDF file and broadcast the success event.
	 * @param orderId
	 * @param documentID
	 */
	documentLoadedSuccess(orderId: number, documentID: string): Observable<void> {
		return new Observable(observer => {
			this.signalRService.registerChannel(`orders:${orderId}`);
			// Get Attachments as a Sync
			// order attachment is different from a document
			this.qualityService.getDocumentPDFAttachments(orderId);
			this.qualityService.getOrderAllDocumentAttachment(orderId, documentID).subscribe(data => {
				if (data.length) {
					const pdf = data.filter(r => r.category === DOCUMENT_ATTACHMENT_CATEGORIES.PDF);
					const photo = data.filter(r => r.category === DOCUMENT_ATTACHMENT_CATEGORIES.PhotoRequest);
					this.store.dispatch(new SetCurrentPhotos(photo));
					if (
						this.qualityService.currentPDFFileId() !== orderId &&
						this.qualityService.isForceReloadCurrentPDF() &&
						pdf.length
					) {
						this.qualityService.downloadPDFFile(orderId, pdf[0].path).subscribe(() => {
							this.broadcastService.broadcast({
								name: 'loadPDFSuccess',
								event: true
							});
						});
						observer.next();
						observer.complete();
					} else {
						observer.next();
						observer.complete();
					}
				} else {
					observer.next();
					observer.complete();
				}
			});
		});

	}

	/**
	 * Kick the user out of the Quality Process
	 */
	private rejectToMainModule(): boolean {
		this.router.navigate(['/intelligence/inbox']);
		return false;
	}
}
